import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { imgList } from "@constants/imgList";
import { LockIcon } from "@components/Icons";
import { useMyTranslation } from "@components/Lang";
import { apiGet, apiPut } from "@apis/gateway";
import { loadingAction } from "@reduxs/Actions/storeAction";
import {
  deviceDetailAction,
  filterDeviceDetailAction,
  mapLocationDevicesAction,
} from "@reduxs/Actions/monitoringAction";
import {
  stateInterface,
  filterDeviceDetailInterface,
} from "@reduxs/interface";

// The metric-specific report fetch each page supplies. It receives the current
// filter/language/id and does its own API call + aggregation + setState (the
// aggregation return shape differs per metric, so the page owns that state).
export type FetchReport = (
  filterDeviceDetail: filterDeviceDetailInterface,
  language: string,
  id: string
) => Promise<void>;

// Shared data-loading logic for the 4 per-metric detail pages
// (temperature / operation / power / iot): device detail + plan + report-detail
// fetch, mute, unit/period lists, and the effect graph. Each page passes its
// own `fetchReport`; everything else here is identical across the four.
export function useDeviceReport(
  fetchReport: FetchReport,
  // Padlock the month range. Only the operation report needs it: a month of
  // its rows (records plus second-precision events) takes the backend past the
  // proxy's 60s ceiling, so the request can only end in a 504. Remove the
  // argument at the OperationDetail call site once weeks/months are read from
  // the pre-generated S3 file rather than queried live.
  { lockMonths = false }: { lockMonths?: boolean } = {}
) {
  const { t, currentLanguage } = useMyTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const language = useSelector(
    (state: stateInterface) => state.storage.language
  );
  const deviceDetail = useSelector(
    (state: stateInterface) => state.monitoring.deviceDetail
  );
  const filterDeviceDetail = useSelector(
    (state: stateInterface) => state.monitoring.filterDeviceDetail
  );
  const [init, setInit] = useState(false);
  const [plan, setPlan] = useState(1);
  // label is a node, not a string: a locked option carries a padlock
  const [listUnit, setListUnit] = useState<
    { value: string; label: React.ReactNode; disabled?: boolean }[]
  >(
    []
  );
  const [listPeriod, setListPeriod] = useState<
    { value: string; label: string }[]
  >([]);
  const [dataReportDetail, setDataReportDetail] = useState<any>({});

  // Always call the latest fetchReport (a fresh page closure each render).
  const fetchReportRef = useRef(fetchReport);
  fetchReportRef.current = fetchReport;

  const getReport = async () => {
    dispatch(loadingAction(true));
    await fetchReportRef.current(filterDeviceDetail, language, id ?? "");
    dispatch(loadingAction(false));
  };
  const getDeviceDetail = async () => {
    const res = await apiGet(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device-report/detail/${id}`,
      {},
      language
    );
    if (res.status && res.code === 200) {
      dispatch(deviceDetailAction(res?.data?.data ?? {}));
      dispatch(
        mapLocationDevicesAction([
          {
            macAddress: res?.data?.data?.deviceInfo?.macAddress ?? "",
            index1: 0,
            index2: 0,
          },
        ])
      );
    } else {
      navigate("/404");
    }
  };
  const getDeviceDetailReport = async () => {
    const res = await apiGet(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device-report/device-detail/${id}`,
      {},
      language
    );
    if (res.status && res.code === 200) {
      setDataReportDetail(res?.data?.data ?? {});
    }
  };
  const getPlan = async () => {
    const res = await apiGet(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device-report/plan/${id}`,
      {},
      language
    );
    if (res.status && res.code === 200) {
      setPlan((res?.data?.data?.plan ?? "standard") === "standard" ? 1 : 2);
    }
  };
  const initData = async () => {
    dispatch(loadingAction(true));
    await getDeviceDetail();
    await getPlan();
    await getDeviceDetailReport();
    setInit(true);
    dispatch(loadingAction(false));
  };
  const onMute = async (macAddress = "") => {
    const params = {};
    dispatch(loadingAction(true));
    await apiPut(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device/${macAddress}/mute-alarm`,
      { ...params },
      {},
      language
    );
    dispatch(loadingAction(false));
  };
  const renderSound = (macAddress = "", soundAlarm = false, alarm = false) => {
    if (alarm) {
      if (soundAlarm) {
        return (
          <img
            src={imgList.ico_sound}
            alt=""
            className="detail__sound detail__sound--active"
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              onMute(macAddress);
            }}
          />
        );
      } else {
        return (
          <img
            src={imgList.ico_sound_disable}
            alt=""
            className="detail__sound"
          />
        );
      }
    } else {
      return <></>;
    }
  };
  const renderListUnit = () => {
    const monthOption = lockMonths
      ? {
          value: "months",
          disabled: true,
          label: (
            <span title={t("detail.monthlocked")}>
              {t("detail.month")}
              <LockIcon />
            </span>
          ),
        }
      : { value: "months", label: t("detail.month") };
    if (plan === 1 || plan === 2) {
      setListUnit([
        { value: "hours", label: t("detail.hour") },
        { value: "days", label: t("detail.day") },
        { value: "weeks", label: t("detail.week") },
        monthOption,
      ]);
    }
  };
  const renderListPeriod = () => {
    const d = new Date();
    let a: any = [];
    const push = (index: number) => {
      a.push({
        value: index,
        label: `${
          index.toString().length < 2 ? `0${index}` : index.toString()
        }:00 - ${
          (index + 1).toString().length < 2
            ? `0${index + 1}`
            : (index + 1).toString()
        }:00`,
      });
    };
    if (dayjs().isSame(filterDeviceDetail.date, "date")) {
      new Array(24).fill("").forEach((e, index) => {
        if (index <= d.getHours()) push(index);
      });
    } else {
      new Array(24).fill("").forEach((e, index) => push(index));
    }
    setListPeriod(a);
  };

  useEffect(() => {
    if (id) {
      initData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    const d = new Date();
    dispatch(
      filterDeviceDetailAction({
        ...filterDeviceDetail,
        ...{
          unit: "hours",
          days: d.getDate().toString(),
          months: (d.getMonth() + 1).toString(),
          years: d.getFullYear().toString(),
          period: d.getHours(),
          date: dayjs(),
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);
  useEffect(() => {
    renderListUnit();
    renderListPeriod();
    if (init) {
      getReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDeviceDetail, init]);
  useEffect(() => {
    renderListUnit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);
  // filterDeviceDetail is shared across the metric pages, so arriving here with
  // "months" still selected from one that allows it would fire the request the
  // padlock exists to prevent. Step back to weeks.
  useEffect(() => {
    if (lockMonths && filterDeviceDetail.unit === "months") {
      dispatch(
        filterDeviceDetailAction({ ...filterDeviceDetail, unit: "weeks" })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockMonths, filterDeviceDetail.unit]);

  return {
    t,
    currentLanguage,
    navigate,
    id,
    dispatch,
    language,
    deviceDetail,
    filterDeviceDetail,
    init,
    plan,
    listUnit,
    listPeriod,
    dataReportDetail,
    renderSound,
    getReport,
  };
}
